import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Button, Table, Typography, Tag, Space, Statistic, Row, Col, Spin, Empty, message, Menu, Dropdown } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, CalendarOutlined, FileOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useTasksSummary } from '../../../src/hooks/useUserRating';
import { userRatingApi } from '../../../src/services/userRatingApi';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const TasksSummaryDashboard = ({ userId, mainViewMode = 'all', mainYear = null, mainPeriod = null }) => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [appliedDateRange, setAppliedDateRange] = useState([null, null]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ['10', '20', '30', '50', '100'],
  });
  
  const actualUserId = userId;
  const startDate = appliedDateRange[0]?.format('YYYY-MM-DD');
  const endDate = appliedDateRange[1]?.format('YYYY-MM-DD');

  const { summaryData, loading, error, refetch } = useTasksSummary(startDate, endDate, actualUserId);


useEffect(() => {
  console.log('TasksSummaryDashboard - Current params:', {
    startDate,
    endDate,
    actualUserId,
    appliedDateRange,
    dateRange
  });
}, [startDate, endDate, actualUserId, appliedDateRange, dateRange]);

useEffect(() => {
  console.log('TasksSummaryDashboard - Summary data updated:', summaryData);
}, [summaryData]);



  // Calculate date range based on main filter
  const getDefaultDateRange = (viewMode, year, period) => {
    if (viewMode === 'all' || !year) {
      return [null, null];
    }

    if (viewMode === 'yearly') {
      // Full year: Jan 1 to Dec 31
      return [
        dayjs(`${year}-01-01`),
        dayjs(`${year}-12-31`)
      ];
    }

    if (viewMode === 'quarterly' && period) {
      // Quarter ranges
      const quarterRanges = {
        'Q1': [`${year}-01-01`, `${year}-03-31`],
        'Q2': [`${year}-04-01`, `${year}-06-30`],
        'Q3': [`${year}-07-01`, `${year}-09-30`],
        'Q4': [`${year}-10-01`, `${year}-12-31`]
      };
      
      const range = quarterRanges[period];
      if (range) {
        return [dayjs(range[0]), dayjs(range[1])];
      }
    }

    if (viewMode === 'monthly' && period) {
      // Month ranges
      const monthMap = {
        'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
        'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
      };
      
      const monthNum = monthMap[period];
      if (monthNum) {
        const firstDay = dayjs(`${year}-${String(monthNum).padStart(2, '0')}-01`);
        const lastDay = firstDay.endOf('month');
        return [firstDay, lastDay];
      }
    }

    return [null, null];
  };

  useEffect(() => {
    const newDateRange = getDefaultDateRange(mainViewMode, mainYear, mainPeriod);
    setDateRange(newDateRange);
    setAppliedDateRange(newDateRange);
 
    if (newDateRange[0] && newDateRange[1]) {
      setIsFiltered(true);
    } else {
      setIsFiltered(false);
    }
  }, [mainViewMode, mainYear, mainPeriod]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleSearch = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Please select both start and end dates');
      return;
    }
    
    setAppliedDateRange(dateRange);
    setIsFiltered(true);
    setPagination(prev => ({ ...prev, current: 1 }));
    message.success('Filtering data for selected date range');
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setAppliedDateRange([null, null]);
    setIsFiltered(false);
    setPagination(prev => ({ ...prev, current: 1 }));
    message.info('Showing all records');
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const formatEfficiency = (percentage) => {
    if (percentage > 0) {
      return `+${percentage}%`;
    }
    return `${percentage}%`;
  };

  const exportToCSV = async () => {
    try {
      message.loading('Preparing enhanced CSV export...', 1);
      
      const allData = await userRatingApi.getTasksSummaryWithDateRange(
        startDate || 'all', 
        endDate || 'all',
        actualUserId
      );

      if (!allData.tasks || allData.tasks.length === 0) {
        message.warning('No data to export');
        return;
      }

      let csvContent = '';

      csvContent += '=== PERFORMANCE OVERVIEW REPORT ===\n';
      csvContent += `"Generated Date","${dayjs().format('YYYY-MM-DD HH:mm:ss')}"\n`;
      csvContent += `"User ID","${actualUserId}"\n`;
      csvContent += `"Report Period","${isFiltered ? `${dayjs(startDate).format('MMM DD')} - ${dayjs(endDate).format('MMM DD, YYYY')}` : `${dayjs(allData.overallSummary.dateRangeStart).format('MMM DD')} - ${dayjs(allData.overallSummary.dateRangeEnd).format('MMM DD, YYYY')}`}"\n`;
      csvContent += '\n';

      if (allData.overallSummary) {
        csvContent += '=== KEY METRICS ===\n';
        csvContent += '"Metric","Value","Description"\n';
        csvContent += `"Total Tasks","${allData.overallSummary.totalTasks}","Total number of completed tasks"\n`;
        csvContent += `"Total Task Versions","${allData.overallSummary.totalTaskVersions}","Sum of all task versions including revisions"\n`;
        csvContent += `"Active Clients","${allData.overallSummary.totalClients}","Number of unique clients worked with"\n`;
        csvContent += `"Active Projects","${allData.overallSummary.totalProjects}","Number of unique projects involved in"\n`;
        csvContent += '\n';

        csvContent += '=== TIME ANALYSIS ===\n';
        csvContent += '"Time Type","Duration","Status"\n';
        csvContent += `"Time Allocated","${allData.overallSummary.totalAssignTime}","Total time originally assigned to tasks"\n`;
        csvContent += `"Time Spent","${allData.overallSummary.totalTakenTime}","Actual time spent completing tasks"\n`;
        
        const timeSaved = allData.overallSummary.overallEfficiencyPercentage > 0 ? 'Time Saved' : 'Time Overrun';
        csvContent += `"Time Variance","${Math.abs(allData.overallSummary.overallEfficiencyPercentage)}%","${timeSaved}"\n`;
        csvContent += '\n';

        csvContent += '=== EFFICIENCY ANALYSIS ===\n';
        csvContent += '"Efficiency Metric","Value","Rating","Performance Level"\n';
        csvContent += `"Overall Efficiency","${formatEfficiency(allData.overallSummary.overallEfficiencyPercentage)}","${allData.overallSummary.overallEfficiencyRating}","${allData.overallSummary.overallEfficiencyPercentage >= 10 ? 'Excellent Performance' : allData.overallSummary.overallEfficiencyPercentage >= 0 ? 'Good Performance' : 'Needs Improvement'}"\n`;
        csvContent += '\n';

        if (allData.tasks.length > 0) {
          const efficiencyStats = allData.tasks.reduce((acc, task) => {
            const rating = task.efficiencyRating.toLowerCase();
            acc[rating] = (acc[rating] || 0) + 1;
            return acc;
          }, {});

          csvContent += '=== TASK EFFICIENCY DISTRIBUTION ===\n';
          csvContent += '"Efficiency Rating","Task Count","Percentage","Performance Indicator"\n';
          csvContent += `"Excellent","${efficiencyStats.excellent || 0}","${((efficiencyStats.excellent || 0) / allData.tasks.length * 100).toFixed(1)}%","Outstanding - Completed ahead of schedule"\n`;
          csvContent += `"Good","${efficiencyStats.good || 0}","${((efficiencyStats.good || 0) / allData.tasks.length * 100).toFixed(1)}%","Satisfactory - Completed on time"\n`;
          csvContent += `"Average","${efficiencyStats.average || 0}","${((efficiencyStats.average || 0) / allData.tasks.length * 100).toFixed(1)}%","Acceptable - Minor delays"\n`;
          csvContent += `"Below Average","${efficiencyStats['below average'] || 0}","${((efficiencyStats['below average'] || 0) / allData.tasks.length * 100).toFixed(1)}%","Concerning - Significant delays"\n`;
          csvContent += `"Incomplete","${efficiencyStats.incomplete || 0}","${((efficiencyStats.incomplete || 0) / allData.tasks.length * 100).toFixed(1)}%","Missing Data - Time tracking incomplete"\n`;
          csvContent += '\n';

          csvContent += '=== PERFORMANCE INSIGHTS ===\n';
          csvContent += '"Insight Type","Analysis","Recommendation"\n';
          
          const excellentPercentage = ((efficiencyStats.excellent || 0) / allData.tasks.length * 100);
          const problemTasks = (efficiencyStats['below average'] || 0) + (efficiencyStats.incomplete || 0);
          
          if (excellentPercentage > 50) {
            csvContent += `"Performance Trend","${excellentPercentage.toFixed(1)}% of tasks completed ahead of schedule","Continue current workflow practices"\n`;
          } else if (problemTasks > allData.tasks.length * 0.3) {
            csvContent += `"Performance Concern","${problemTasks} tasks (${(problemTasks/allData.tasks.length*100).toFixed(1)}%) need attention","Review time estimation and tracking processes"\n`;
          } else {
            csvContent += `"Performance Status","Balanced performance across efficiency ratings","Monitor trends and optimize where possible"\n`;
          }
          
          csvContent += `"Data Completeness","${allData.tasks.length - (efficiencyStats.incomplete || 0)} of ${allData.tasks.length} tasks have complete time data","${efficiencyStats.incomplete ? 'Improve time tracking completion' : 'Excellent data quality'}"\n`;
          csvContent += '\n';
        }
      }

      csvContent += '=== DETAILED TASK DATA ===\n';
      
      const taskHeaders = [
        'Task ID', 'Client Name', 'Project Name', 'Task Name', 'Total Versions', 'Latest Version',
        'Start Date', 'End Date', 'Assigned Time', 'Taken Time', 'Efficiency %', 'Efficiency Rating',
        'Time Variance', 'Performance Status'
      ];

      csvContent += taskHeaders.map(header => `"${header}"`).join(',') + '\n';

      const csvTaskData = allData.tasks.map(task => {
        const variance = task.efficiencyPercentage > 0 ? `+${task.efficiencyPercentage}% (Ahead)` : 
                        task.efficiencyPercentage < 0 ? `${task.efficiencyPercentage}% (Behind)` : 'On Time';
        
        const status = task.efficiencyRating === 'Excellent' ? 'Outstanding' :
                      task.efficiencyRating === 'Good' ? 'Satisfactory' :
                      task.efficiencyRating === 'Average' ? 'Acceptable' :
                      task.efficiencyRating === 'Below Average' ? 'Needs Improvement' : 'Incomplete Data';

        return [
          task.taskId,
          task.clientName,
          task.projectName,
          task.taskName,
          task.totalVersions,
          task.latestVersion,
          dayjs(task.taskStartDate).format('YYYY-MM-DD'),
          dayjs(task.taskEndDate).format('YYYY-MM-DD'),
          task.assignTime,
          task.takenTime,
          `${task.efficiencyPercentage}%`,
          task.efficiencyRating,
          variance,
          status
        ];
      });

      csvContent += csvTaskData
        .map(row => row.map(cell => `"${cell || ''}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `performance_overview_${dayjs().format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success(`Successfully exported enhanced performance overview + ${allData.tasks.length} tasks to CSV!`);
    } catch (error) {
      console.error('CSV Export Error:', error);
      message.error('Failed to export CSV. Please try again.');
    }
  };

  // Export ALL data to Excel function
  const exportToExcel = async () => {
    try {
      message.loading('Preparing enhanced Excel export...', 1);

      if (typeof XLSX === 'undefined') {
        message.error('Excel export requires XLSX library. Please install it first.');
        return;
      }

      const allData = await userRatingApi.getTasksSummaryWithDateRange(
        startDate || 'all', 
        endDate || 'all',
        actualUserId
      );

      if (!allData.tasks || allData.tasks.length === 0) {
        message.warning('No data to export');
        return;
      }

      const wb = XLSX.utils.book_new();

      if (allData.overallSummary) {
        const dashboardData = [
          { 'Section': '=== PERFORMANCE OVERVIEW DASHBOARD ===', 'Detail': '', 'Value': '', 'Status': '' },
          { 'Section': 'Report Information', 'Detail': 'Generated Date', 'Value': dayjs().format('YYYY-MM-DD HH:mm:ss'), 'Status': 'Current' },
          { 'Section': 'Report Information', 'Detail': 'User ID', 'Value': actualUserId, 'Status': 'Active' },
          { 'Section': 'Report Information', 'Detail': 'Report Period', 'Value': isFiltered ? 
            `${dayjs(startDate).format('MMM DD')} - ${dayjs(endDate).format('MMM DD, YYYY')}` : 
            `${dayjs(allData.overallSummary.dateRangeStart).format('MMM DD')} - ${dayjs(allData.overallSummary.dateRangeEnd).format('MMM DD, YYYY')}`, 
            'Status': 'Complete' },
          { 'Section': '', 'Detail': '', 'Value': '', 'Status': '' },
          
          { 'Section': '=== KEY METRICS (Dashboard Cards) ===', 'Detail': '', 'Value': '', 'Status': '' },
          { 'Section': 'Total Tasks', 'Detail': 'Completed tasks in period', 'Value': allData.overallSummary.totalTasks, 'Status': allData.overallSummary.totalTasks > 10 ? 'High Activity' : 'Standard Activity' },
          { 'Section': 'Total Task Versions', 'Detail': 'Including all revisions', 'Value': allData.overallSummary.totalTaskVersions, 'Status': allData.overallSummary.totalTaskVersions > allData.overallSummary.totalTasks ? 'Revisions Present' : 'First-time Completion' },
          { 'Section': 'Active Clients', 'Detail': 'Unique clients served', 'Value': allData.overallSummary.totalClients, 'Status': allData.overallSummary.totalClients > 5 ? 'Diverse Portfolio' : 'Focused Portfolio' },
          { 'Section': 'Active Projects', 'Detail': 'Concurrent project involvement', 'Value': allData.overallSummary.totalProjects, 'Status': allData.overallSummary.totalProjects > 3 ? 'Multi-Project' : 'Focused Work' },
          { 'Section': '', 'Detail': '', 'Value': '', 'Status': '' },
          
          { 'Section': '=== TIME ANALYSIS (Time Comparison) ===', 'Detail': '', 'Value': '', 'Status': '' },
          { 'Section': 'Time Allocated', 'Detail': 'Original time assignment', 'Value': allData.overallSummary.totalAssignTime, 'Status': 'Baseline' },
          { 'Section': 'Time Spent', 'Detail': 'Actual time investment', 'Value': allData.overallSummary.totalTakenTime, 'Status': allData.overallSummary.overallEfficiencyPercentage >= 0 ? 'Within/Under Budget' : 'Over Budget' },
          { 'Section': 'Time Variance', 'Detail': 'Efficiency calculation', 'Value': `${Math.abs(allData.overallSummary.overallEfficiencyPercentage)}%`, 'Status': allData.overallSummary.overallEfficiencyPercentage > 0 ? 'Time Saved' : 'Time Overrun' },
          { 'Section': '', 'Detail': '', 'Value': '', 'Status': '' },
          
          { 'Section': '=== OVERALL EFFICIENCY (Central Badge) ===', 'Detail': '', 'Value': '', 'Status': '' },
          { 'Section': 'Efficiency Percentage', 'Detail': 'Performance calculation', 'Value': `${allData.overallSummary.overallEfficiencyPercentage}%`, 'Status': formatEfficiency(allData.overallSummary.overallEfficiencyPercentage) },
          { 'Section': 'Efficiency Rating', 'Detail': 'Performance classification', 'Value': allData.overallSummary.overallEfficiencyRating, 'Status': allData.overallSummary.overallEfficiencyPercentage >= 10 ? 'Outstanding' : allData.overallSummary.overallEfficiencyPercentage >= 0 ? 'Satisfactory' : 'Needs Improvement' },
          { 'Section': '', 'Detail': '', 'Value': '', 'Status': '' },
          
          { 'Section': '=== PERFORMANCE INSIGHTS ===', 'Detail': '', 'Value': '', 'Status': '' },
          { 'Section': 'Trend Analysis', 'Detail': 'Overall performance direction', 'Value': allData.overallSummary.overallEfficiencyPercentage >= 10 ? 'Consistently ahead of schedule' : allData.overallSummary.overallEfficiencyPercentage >= 0 ? 'Meeting deadlines effectively' : 'Time management opportunities exist', 'Status': allData.overallSummary.overallEfficiencyPercentage >= 0 ? 'Positive' : 'Improvement Needed' }
        ];

        const dashboardSheet = XLSX.utils.json_to_sheet(dashboardData);
        dashboardSheet['!cols'] = [
          { wch: 35 },
          { wch: 30 },
          { wch: 25 },
          { wch: 20 }
        ];
        XLSX.utils.book_append_sheet(wb, dashboardSheet, 'Executive Dashboard');
      }

      if (allData.tasks.length > 0) {
        const efficiencyStats = allData.tasks.reduce((acc, task) => {
          const rating = task.efficiencyRating.toLowerCase();
          acc[rating] = (acc[rating] || 0) + 1;
          return acc;
        }, {});

        const distributionData = [
          { 'Efficiency Rating': '=== TASK EFFICIENCY DISTRIBUTION ===', 'Count': '', 'Percentage': '', 'Performance Indicator': '', 'Color Code': '' },
          { 'Efficiency Rating': 'Excellent', 'Count': efficiencyStats.excellent || 0, 'Percentage': `${((efficiencyStats.excellent || 0) / allData.tasks.length * 100).toFixed(1)}%`, 'Performance Indicator': 'Outstanding - Completed ahead of schedule', 'Color Code': 'Green (Success)' },
          { 'Efficiency Rating': 'Good', 'Count': efficiencyStats.good || 0, 'Percentage': `${((efficiencyStats.good || 0) / allData.tasks.length * 100).toFixed(1)}%`, 'Performance Indicator': 'Satisfactory - Completed on time', 'Color Code': 'Blue (Standard)' },
          { 'Efficiency Rating': 'Average', 'Count': efficiencyStats.average || 0, 'Percentage': `${((efficiencyStats.average || 0) / allData.tasks.length * 100).toFixed(1)}%`, 'Performance Indicator': 'Acceptable - Minor delays', 'Color Code': 'Yellow (Caution)' },
          { 'Efficiency Rating': 'Below Average', 'Count': efficiencyStats['below average'] || 0, 'Percentage': `${((efficiencyStats['below average'] || 0) / allData.tasks.length * 100).toFixed(1)}%`, 'Performance Indicator': 'Concerning - Significant delays', 'Color Code': 'Red (Alert)' },
          { 'Efficiency Rating': 'Incomplete', 'Count': efficiencyStats.incomplete || 0, 'Percentage': `${((efficiencyStats.incomplete || 0) / allData.tasks.length * 100).toFixed(1)}%`, 'Performance Indicator': 'Missing Data - Time tracking incomplete', 'Color Code': 'Gray (No Data)' },
          { 'Efficiency Rating': '', 'Count': '', 'Percentage': '', 'Performance Indicator': '', 'Color Code': '' },
          { 'Efficiency Rating': 'TOTALS', 'Count': allData.tasks.length, 'Percentage': '100.0%', 'Performance Indicator': 'Complete analysis coverage', 'Color Code': 'Summary' },
          { 'Efficiency Rating': '', 'Count': '', 'Percentage': '', 'Performance Indicator': '', 'Color Code': '' },
          
          { 'Efficiency Rating': '=== PERFORMANCE ANALYSIS ===', 'Count': '', 'Percentage': '', 'Performance Indicator': '', 'Color Code': '' },
          { 'Efficiency Rating': 'High Performance', 'Count': (efficiencyStats.excellent || 0) + (efficiencyStats.good || 0), 'Percentage': `${(((efficiencyStats.excellent || 0) + (efficiencyStats.good || 0)) / allData.tasks.length * 100).toFixed(1)}%`, 'Performance Indicator': 'Tasks meeting or exceeding expectations', 'Color Code': 'Positive Trend' },
          { 'Efficiency Rating': 'Needs Attention', 'Count': (efficiencyStats['below average'] || 0) + (efficiencyStats.incomplete || 0), 'Percentage': `${(((efficiencyStats['below average'] || 0) + (efficiencyStats.incomplete || 0)) / allData.tasks.length * 100).toFixed(1)}%`, 'Performance Indicator': 'Tasks requiring process improvement', 'Color Code': 'Action Required' }
        ];

        const distributionSheet = XLSX.utils.json_to_sheet(distributionData);
        distributionSheet['!cols'] = [
          { wch: 25 },
          { wch: 12 },
          { wch: 12 },
          { wch: 40 },
          { wch: 18 }
        ];
        XLSX.utils.book_append_sheet(wb, distributionSheet, 'Efficiency Distribution');
      }

      const enhancedTaskData = allData.tasks.map(task => {
        const variance = task.efficiencyPercentage > 0 ? `+${task.efficiencyPercentage}% (Ahead)` : 
                        task.efficiencyPercentage < 0 ? `${task.efficiencyPercentage}% (Behind)` : 'On Time';
        
        const performanceLevel = task.efficiencyRating === 'Excellent' ? 'Outstanding Performance' :
                               task.efficiencyRating === 'Good' ? 'Satisfactory Performance' :
                               task.efficiencyRating === 'Average' ? 'Acceptable Performance' :
                               task.efficiencyRating === 'Below Average' ? 'Needs Improvement' : 'Incomplete Data';

        const priorityFlag = task.efficiencyRating === 'Below Average' || task.efficiencyRating === 'Incomplete' ? 'HIGH' :
                           task.efficiencyRating === 'Average' ? 'MEDIUM' : 'LOW';

        return {
          'Task ID': task.taskId,
          'Client Name': task.clientName,
          'Project Name': task.projectName,
          'Task Name': task.taskName,
          'Total Versions': task.totalVersions,
          'Latest Version': task.latestVersion,
          'Start Date': dayjs(task.taskStartDate).format('YYYY-MM-DD'),
          'End Date': dayjs(task.taskEndDate).format('YYYY-MM-DD'),
          'Assigned Time': task.assignTime,
          'Taken Time': task.takenTime,
          'Efficiency %': `${task.efficiencyPercentage}%`,
          'Efficiency Rating': task.efficiencyRating,
          'Time Variance': variance,
          'Performance Level': performanceLevel,
          'Review Priority': priorityFlag,
          'Duration (Days)': dayjs(task.taskEndDate).diff(dayjs(task.taskStartDate), 'days') + 1
        };
      });

      const taskSheet = XLSX.utils.json_to_sheet(enhancedTaskData);
      taskSheet['!cols'] = [
        { wch: 10 },
        { wch: 18 },
        { wch: 25 },
        { wch: 30 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 13 },
        { wch: 13 },
        { wch: 12 },
        { wch: 15 },
        { wch: 18 },
        { wch: 20 },
        { wch: 12 },
        { wch: 12 }
      ];
      XLSX.utils.book_append_sheet(wb, taskSheet, 'Detailed Task Analysis');

      const managementSummary = [
        { 'KPI': '=== EXECUTIVE SUMMARY ===', 'Current Value': '', 'Target/Benchmark': '', 'Status': '', 'Recommendation': '' },
        { 'KPI': 'Overall Efficiency', 'Current Value': allData.overallSummary.overallEfficiencyRating, 'Target/Benchmark': 'Good or Higher', 'Status': allData.overallSummary.overallEfficiencyPercentage >= 0 ? 'Meeting Standard' : 'Below Standard', 'Recommendation': allData.overallSummary.overallEfficiencyPercentage >= 10 ? 'Maintain excellence' : allData.overallSummary.overallEfficiencyPercentage >= 0 ? 'Continue current practices' : 'Review time management processes' },
        { 'KPI': 'Time Management', 'Current Value': `${allData.overallSummary.overallEfficiencyPercentage}%`, 'Target/Benchmark': '0% or Higher', 'Status': allData.overallSummary.overallEfficiencyPercentage >= 10 ? 'Excellent' : allData.overallSummary.overallEfficiencyPercentage >= 0 ? 'Good' : 'Needs Improvement', 'Recommendation': allData.overallSummary.overallEfficiencyPercentage < 0 ? 'Implement time tracking improvements' : 'Optimize current workflows' },
        { 'KPI': 'Project Delivery', 'Current Value': `${allData.tasks.length} tasks completed`, 'Target/Benchmark': 'Consistent delivery', 'Status': 'Active', 'Recommendation': 'Monitor delivery consistency' },
        { 'KPI': 'Client Portfolio', 'Current Value': `${allData.overallSummary.totalClients} clients`, 'Target/Benchmark': 'Diversified engagement', 'Status': allData.overallSummary.totalClients > 3 ? 'Diversified' : 'Focused', 'Recommendation': allData.overallSummary.totalClients <= 2 ? 'Consider expanding client base' : 'Maintain client relationships' },
        { 'KPI': '', 'Current Value': '', 'Target/Benchmark': '', 'Status': '', 'Recommendation': '' },
        { 'KPI': '=== ACTION ITEMS ===', 'Current Value': '', 'Target/Benchmark': '', 'Status': '', 'Recommendation': '' }
      ];

      if (allData.overallSummary.overallEfficiencyPercentage < 0) {
        managementSummary.push({ 'KPI': 'Priority Action', 'Current Value': 'Time Overruns Detected', 'Target/Benchmark': 'Immediate Review', 'Status': 'High Priority', 'Recommendation': 'Analyze task estimation accuracy and resource allocation' });
      }
      
      const problematicTasks = allData.tasks.filter(task => 
        task.efficiencyRating === 'Below Average' || task.efficiencyRating === 'Incomplete'
      );
      
      if (problematicTasks.length > 0) {
        managementSummary.push({ 'KPI': 'Task Review', 'Current Value': `${problematicTasks.length} tasks need attention`, 'Target/Benchmark': 'Zero problematic tasks', 'Status': 'Action Required', 'Recommendation': 'Review specific tasks flagged as Below Average or Incomplete' });
      }

      managementSummary.push({ 'KPI': 'Next Review', 'Current Value': 'Recommended', 'Target/Benchmark': 'Weekly/Monthly', 'Status': 'Scheduled', 'Recommendation': 'Regular performance monitoring and adjustment' });

      const summarySheet = XLSX.utils.json_to_sheet(managementSummary);
      summarySheet['!cols'] = [
        { wch: 25 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 35 }
      ];
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Management Summary');

      XLSX.writeFile(wb, `performance_dashboard_complete_${dayjs().format('YYYY-MM-DD')}.xlsx`);
      message.success(`Successfully exported enhanced performance dashboard with ${allData.tasks.length} tasks to Excel!`);
    } catch (error) {
      console.error('Excel Export Error:', error);
      message.error('Failed to export to Excel. Please try CSV export instead.');
    }
  };

  const ExportButton = () => {
    const [dropdownVisible, setDropdownVisible] = useState(false);

    const menu = (
      <Menu>
        <Menu.Item key="csv" onClick={exportToCSV}>
          <FileOutlined /> Export Complete Report (CSV)
        </Menu.Item>
        <Menu.Item key="excel" onClick={exportToExcel}>
          <FileExcelOutlined /> Export Complete Report (Excel)
        </Menu.Item>
      </Menu>
    );

    return (
      <Dropdown 
        overlay={menu} 
        trigger={['click']}
        visible={dropdownVisible}
        onVisibleChange={setDropdownVisible}
      >
        <Button 
          type="text" 
          icon={<DownloadOutlined />}
          size="small"
          onClick={(e) => e.preventDefault()}
        >
          Export Complete Report
        </Button>
      </Dropdown>
    );
  };

  const getEfficiencyColor = (rating) => {
    switch (rating?.toLowerCase()) {
      case 'excellent': return '#52c41a';
      case 'good': return '#1890ff';
      case 'average': return '#faad14';
      case 'below average': return '#ff4d4f';
      case 'incomplete': return '#d9d9d9';
      default: return '#d9d9d9';
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'taskId',
      key: 'taskId',
      width: 60,
      fixed: 'left',
      render: (text) => <Text strong style={{ color: '#595959' }}>{text}</Text>
    },
    {
      title: 'Client',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 120,
      ellipsis: true,
      render: (text) => <Text style={{ fontWeight: 500 }}>{text}</Text>
    },
    {
      title: 'Project',
      dataIndex: 'projectName',
      key: 'projectName',
      width: 140,
      ellipsis: true
    },
    {
      title: 'Task Name',
      dataIndex: 'taskName',
      key: 'taskName',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <div style={{ maxWidth: 180, wordWrap: 'break-word' }}>
          <Text>{text}</Text>
        </div>
      )
    },
    {
      title: 'Versions',
      dataIndex: 'totalVersions',
      key: 'totalVersions',
      width: 80,
      align: 'center',
      render: (total, record) => (
        <div style={{ textAlign: 'center' }}>
          <Tag style={{ fontSize: '12px', margin: 0 }}>
            V{String(record.latestVersion).padStart(2, '0')}
          </Tag>
          {total > 1 && (
            <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
              ({total} total)
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Start Date',
      dataIndex: 'taskStartDate',
      key: 'taskStartDate',
      width: 100,
      render: (date) => dayjs(date).format('MMM DD')
    },
    {
      title: 'End Date',
      dataIndex: 'taskEndDate',
      key: 'taskEndDate',
      width: 100,
      render: (date) => dayjs(date).format('MMM DD')
    },
    {
      title: 'Assigned Time',
      dataIndex: 'assignTime',
      key: 'assignTime',
      width: 110,
      align: 'center',
      render: (time) => <Text style={{ fontFamily: 'monospace' }}>{time}</Text>
    },
    {
      title: 'Taken Time',
      dataIndex: 'takenTime',
      key: 'takenTime',
      width: 110,
      align: 'center',
      render: (time) => <Text style={{ fontFamily: 'monospace' }}>{time}</Text>
    },
    {
      title: 'Efficiency',
      dataIndex: 'efficiencyPercentage',
      key: 'efficiency',
      width: 120,
      align: 'center',
      render: (percentage, record) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: 'bold',
            color: percentage >= 0 ? '#52c41a' : '#ff4d4f'
          }}>
            {formatEfficiency(percentage)}
          </div>
          <Tag 
            color={getEfficiencyColor(record.efficiencyRating)}
            style={{ 
              fontSize: '11px',
              padding: '1px 4px',
              marginTop: 2,
              border: 'none'
            }}
          >
            {record.efficiencyRating}
          </Tag>
        </div>
      )
    }
  ];

  useEffect(() => {
    if (summaryData.tasks) {
      setPagination(prev => ({
        ...prev,
        total: summaryData.tasks.length
      }));
    }
  }, [summaryData.tasks]);

  if (error) {
    return (
      <div style={{ padding: 24, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        <Card>
          <Empty 
            description={`Error: ${error}`}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 0, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {summaryData.overallSummary && (
        <Card 
          className="summary-card"
          style={{ 
            marginBottom: 24,
            background: 'linear-gradient(135deg, #DDEFFF 0%, #99c8f1ff 100%)',
            border: 'none',
            borderRadius: '16px',
            color: 'white'
          }}
          bodyStyle={{ padding: '32px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Space size="large">
              <div>
                <Text strong style={{ marginRight: 8, color: '#000' }}>Date Range:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  style={{ width: 250 }}
                  placeholder={['Start Date', 'End Date']}
                />
              </div>
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                disabled={!dateRange || !dateRange[0] || !dateRange[1]}
              >
                Search
              </Button>
            </Space>
            <Space>
              <Button 
                onClick={handleClearFilters}
                disabled={!isFiltered}
              >
                Show All Records
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={refetch}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </div>

          <div style={{ marginBottom: 32, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ 
                background: 'rgba(255,255,255,0.2)', 
                padding: '12px', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CalendarOutlined style={{ color: '#000000', fontSize: '24px' }} />
              </div>
              <Title level={2} style={{ margin: 0, color: '#000000', fontWeight: 600 }}>
                Performance Overview
              </Title>
            </div>
            {isFiltered ? (
              <Text style={{ color: '#000000', fontSize: '16px' }}>
                Period: {dayjs(startDate).format('MMM DD')} - {dayjs(endDate).format('MMM DD, YYYY')}
              </Text>
            ) : (
              <Text style={{ color: '#000000', fontSize: '16px' }}>
                Showing data from {dayjs(summaryData.overallSummary.dateRangeStart).format('MMM DD')} to {dayjs(summaryData.overallSummary.dateRangeEnd).format('MMM DD, YYYY')}
              </Text>
            )}
          </div>

          <Row gutter={[32, 24]} style={{ marginBottom: 32 }}>
            <Col xs={12} sm={6}>
              <div style={{ 
                background: 'rgba(255,255,255,0.25)', 
                padding: '20px', 
                borderRadius: '12px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#000000', lineHeight: 1 }}>
                  {summaryData.overallSummary.totalTasks}
                </div>
                <Text style={{ color: '#000000', fontSize: '14px', fontWeight: 500 }}>
                  Total Tasks
                </Text>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ 
                background: 'rgba(255,255,255,0.25)', 
                padding: '20px', 
                borderRadius: '12px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#000000', lineHeight: 1 }}>
                  {summaryData.overallSummary.totalTaskVersions}
                </div>
                <Text style={{ color: '#000000', fontSize: '14px', fontWeight: 500 }}>
                  Total Versions
                </Text>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ 
                background: 'rgba(255,255,255,0.25)', 
                padding: '20px', 
                borderRadius: '12px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#000000', lineHeight: 1 }}>
                  {summaryData.overallSummary.totalClients}
                </div>
                <Text style={{ color: '#000000', fontSize: '14px', fontWeight: 500 }}>
                  Active Clients
                </Text>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ 
                background: 'rgba(255,255,255,0.25)', 
                padding: '20px', 
                borderRadius: '12px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#000000', lineHeight: 1 }}>
                  {summaryData.overallSummary.totalProjects}
                </div>
                <Text style={{ color: '#000000', fontSize: '14px', fontWeight: 500 }}>
                  Active Projects
                </Text>
              </div>
            </Col>
          </Row>
          
          <div style={{ 
            background: 'rgba(255,255,255,0.1)', 
            padding: '28px', 
            borderRadius: '16px',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255,255,255,0.40)'
          }}>
            <Row gutter={[32, 24]} align="middle">
              <Col xs={24} md={16}>
                <Row gutter={[24, 16]}>
                  <Col xs={12}>
                    <div style={{ textAlign: 'center' }}>
                      <Text style={{ color: '#000000', fontSize: '14px', display: 'block', marginBottom: 8 }}>
                        Time Allocated
                      </Text>
                      <div style={{ 
                        fontSize: '28px', 
                        fontWeight: 'bold', 
                        color: '#4285FF',
                        fontFamily: 'monospace',
                      }}>
                        {summaryData.overallSummary.totalAssignTime}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <div style={{ textAlign: 'center' }}>
                      <Text style={{ color: '#000000', fontSize: '14px', display: 'block', marginBottom: 8 }}>
                        Time Spent
                      </Text>
                      <div style={{ 
                        fontSize: '28px', 
                        fontWeight: 'bold', 
                        color: '#EB8F20',
                        fontFamily: 'monospace',
                      }}>
                        {summaryData.overallSummary.totalTakenTime}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
              
              <Col xs={24} md={8}>
                <div style={{ 
                  textAlign: 'center',
                  padding: '24px',
                  background: 'rgba(255,255,255,0.3)',
                  borderRadius: '20px',
                  border: '2px solid rgba(255,255,255,0.3)'
                }}>
                  <Text style={{ color: '#000000', fontSize: '16px', display: 'block', marginBottom: 12 }}>
                    Overall Efficiency
                  </Text>
                  <div style={{ 
                    fontSize: '36px', 
                    fontWeight: 'bold', 
                    color: summaryData.overallSummary.overallEfficiencyPercentage >= 10 ? '#14B253' : 
                           summaryData.overallSummary.overallEfficiencyPercentage >= 0 ? '#080807ff' : '#FFB6C1',
                    marginBottom: 12,
                  }}>
                    {formatEfficiency(summaryData.overallSummary.overallEfficiencyPercentage)}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '6px 16px',
                    background: summaryData.overallSummary.overallEfficiencyPercentage >= 10 ? '#14B253' : 
                               summaryData.overallSummary.overallEfficiencyPercentage >= 0 ? '#FF9393' : '#D83E3E',
                    borderRadius: '20px',
                    border: `1px solid ${
                      summaryData.overallSummary.overallEfficiencyPercentage >= 10 ? '#14B253' : 
                      summaryData.overallSummary.overallEfficiencyPercentage >= 0 ? '#FF9393' : '#D83E3E'
                    }`,
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 600,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}>
                    {summaryData.overallSummary.overallEfficiencyRating}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ fontSize: '16px' }}>
              Task Details ({summaryData.tasks?.length || 0} total tasks)
            </Text>
            <ExportButton />
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" tip="Loading tasks data..." />
          </div>
        ) : summaryData.tasks && summaryData.tasks.length > 0 ? (
          <Table
            columns={columns}
            dataSource={summaryData.tasks}
            rowKey="taskId"
            scroll={{ x: 1200 }}
            pagination={{
              ...pagination,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} tasks`,
              size: 'small',
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize
                }));
              },
              onShowSizeChange: (current, size) => {
                setPagination(prev => ({
                  ...prev,
                  current: 1,
                  pageSize: size
                }));
              }
            }}
            onChange={handleTableChange}
            size="small"
          />
        ) : (
          <Empty 
            description={
              isFiltered 
                ? "No tasks found for the selected date range"
                : "No tasks available"
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
    </div>
  );
};

export default TasksSummaryDashboard;